import { useParams, Link } from 'react-router-dom';
import { tvShows } from '@/mocks/tvShows';
import ShowCard from '@/components/feature/ShowCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const show = tvShows.find((s) => s.id === id);

  if (!show) {
    return (
      <div className="w-full px-4 md:px-6 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">剧集未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您查找的剧集不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedShows = tvShows
    .filter((s) => s.id !== show.id && (s.categorySlug === show.categorySlug || s.tags.some((t) => show.tags.includes(t))))
    .slice(0, 6);

  const statusText = () => {
    if (show.status === 'completed') return `全${show.episodeCount}集`;
    if (show.status === 'updating') return `更新至${show.currentEpisode}集/共${show.episodeCount}集`;
    return `更新至${show.currentEpisode}集/共${show.episodeCount}集`;
  };

  const itemListElement = Array.from({ length: show.episodeCount }, (_, i) => ({
    '@type': 'ListItem' as const,
    position: i + 1,
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${show.title} - 天堂影视`,
    description: show.description,
    author: {
      '@type': 'Person',
      name: `导演：${show.director}`,
    },
    datePublished: `${show.year}-01-01`,
    publisher: {
      '@type': 'Organization',
      name: '天堂影视',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.tiantangyingshi.com/detail/${show.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <article>
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: show.category, href: `/category/${show.categorySlug}` },
                  { label: show.title },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="w-full bg-background-100">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="shrink-0">
                  <div className="w-48 sm:w-56 rounded-lg overflow-hidden mx-auto lg:mx-0" data-product-shop="">
                    <img
                      src={show.coverImage}
                      alt={show.title}
                      title={`${show.title} - 天堂影视电视剧全集免费在线观看`}
                      className="w-full aspect-[3/4] object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                    {show.title}
                  </h1>
                  <p className="text-sm text-foreground-400 mb-4">{show.subTitle}</p>

                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    {show.status === 'hot' && (
                      <span className="bg-primary-500 text-background-50 text-xs px-2.5 py-1 rounded-md font-medium whitespace-nowrap">热播中</span>
                    )}
                    {show.status === 'updating' && (
                      <span className="bg-accent-500 text-background-50 text-xs px-2.5 py-1 rounded-md font-medium whitespace-nowrap">连载中</span>
                    )}
                    {show.status === 'completed' && (
                      <span className="bg-secondary-500 text-background-50 text-xs px-2.5 py-1 rounded-md font-medium whitespace-nowrap">已完结</span>
                    )}
                    <span className="flex items-center gap-1 text-amber-500 text-sm font-semibold bg-amber-50 px-2 py-0.5 rounded-md">
                      <i className="ri-star-fill text-xs"></i>
                      {show.rating}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                    <div>
                      <span className="text-xs text-foreground-400">年份</span>
                      <p className="text-sm font-medium text-foreground-800">{show.year}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">地区</span>
                      <p className="text-sm font-medium text-foreground-800">{show.region}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">导演</span>
                      <p className="text-sm font-medium text-foreground-800">{show.director}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">集数</span>
                      <p className="text-sm font-medium text-foreground-800">{statusText()}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">更新</span>
                      <p className="text-sm font-medium text-foreground-800">{show.updateInfo}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">分类</span>
                      <Link
                        to={`/category/${show.categorySlug}`}
                        className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        {show.category}
                      </Link>
                    </div>
                  </div>

                  <div className="mb-5">
                    <p className="text-xs text-foreground-400 mb-1">主演</p>
                    <div className="flex flex-wrap gap-1.5">
                      {show.cast.map((actor) => (
                        <span
                          key={actor}
                          className="text-xs px-2.5 py-1 bg-background-50 text-foreground-700 rounded-full border border-background-200/70"
                        >
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {show.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即观看
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-4">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-4xl">
                {show.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-100">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-5">
                分集列表
              </h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {Array.from({ length: show.episodeCount }, (_, i) => (
                  <button
                    key={i}
                    className={`py-2.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      i < show.currentEpisode
                        ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                        : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                    }`}
                    disabled={i >= show.currentEpisode}
                  >
                    第{i + 1}集
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {relatedShows.length > 0 && (
          <div className="w-full bg-background-50">
            <div className="w-full px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedShows.map((s) => (
                    <div key={s.id} data-product-shop="">
                      <ShowCard show={s} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </article>
    </>
  );
}